import { normalizeRoutePath } from '../../../utils/routeUtils'

/** sessionStorage: initial workspace landing evaluated once per browser session. */
export const WORKSPACE_LANDING_HANDLED_SESSION_KEY = 'app_workspace_landing_handled'

export const WORKSPACE_ENTERED_SESSION_KEY = 'app_workspace_entered_session'

/** Paths used only for the one-time post-login / stale-URL landing (not ongoing navigation). */
export const STALE_WORKSPACE_ENTRY_PATHS = ['/', '/company', '/company/overview'] as const

export function isStaleWorkspaceEntryPath(path: string): boolean {
  const normalized = normalizeRoutePath(path)
  return (STALE_WORKSPACE_ENTRY_PATHS as readonly string[]).includes(normalized)
}

export function isWorkspaceLandingHandled(): boolean {
  if (typeof sessionStorage === 'undefined') return false
  return sessionStorage.getItem(WORKSPACE_LANDING_HANDLED_SESSION_KEY) === '1'
}

export function markWorkspaceLandingHandled(): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(WORKSPACE_LANDING_HANDLED_SESSION_KEY, '1')
}

export function clearWorkspaceLandingHandled(): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(WORKSPACE_LANDING_HANDLED_SESSION_KEY)
}

export function clearWorkspaceEnteredSession(): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(WORKSPACE_ENTERED_SESSION_KEY)
}

/**
 * One-time landing target after login or stale URL on workspace entry.
 * Intentional navigation to /company/overview later must not call this again
 * (gate with isWorkspaceLandingHandled).
 */
export function getOneTimeWorkspaceLandingRedirect(
  path: string,
  hasCompany: boolean,
  opts: { companyBlocked?: boolean; registrationClosed?: boolean } = {},
): string | null {
  const normalized = normalizeRoutePath(path)
  // A member of a suspended company still has a company: never offer them the create form,
  // which cloud would refuse anyway (SC-409).
  const memberOfCompany = hasCompany || opts.companyBlocked === true
  if (memberOfCompany && isStaleWorkspaceEntryPath(normalized)) {
    return '/dashboard'
  }
  if (!memberOfCompany && normalized === '/') {
    // Registration Mode Closed is the go-to-market waitlist: cloud refuses self-serve company
    // creation, so the create form would only fail on submit (SC-443).
    return opts.registrationClosed === true ? '/dashboard' : '/company/setup'
  }
  return null
}

/** @deprecated Use isStaleWorkspaceEntryPath — kept for tests naming continuity */
export function isGenericWorkspaceEntryPath(path: string): boolean {
  return isStaleWorkspaceEntryPath(path)
}

/** @deprecated Use getOneTimeWorkspaceLandingRedirect with session gate */
export function shouldRedirectToDashboardLanding(path: string, hasCompany: boolean): boolean {
  if (!hasCompany) return false
  return isStaleWorkspaceEntryPath(path)
}

/** @deprecated Use getOneTimeWorkspaceLandingRedirect with session gate */
export function getWorkspaceLandingRedirectPath(
  path: string,
  hasCompany: boolean,
): string | null {
  return getOneTimeWorkspaceLandingRedirect(path, hasCompany)
}

/** Sidebar "My Company" highlight when any company section except Create Company setup. */
export function isCompanyManagementNavActive(section: string): boolean {
  return section.startsWith('company') && section !== 'companySetup'
}
